import { useCallback, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { ArrowLeft, LogOut, Plus } from "lucide-react";
import { PageShell } from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import { PricingRegister } from "@/components/admin/PricingRegister";
import { QuoteBuilder } from "@/components/admin/QuoteBuilder";
import { QuoteList } from "@/components/admin/QuoteList";
import { CommercialPanel } from "@/components/admin/CommercialPanel";

export default function AdminQuotesPage() {
  const { user, isAdmin, loading, logout } = useAuth();
  const [tab, setTab] = useState("builder");
  const [openedQuote, setOpenedQuote] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleSaved = useCallback((q) => {
    setOpenedQuote(q);
    setRefreshKey((k) => k + 1);
  }, []);

  if (loading) {
    return (
      <PageShell>
        <div className="container py-14 text-sm text-muted-foreground">Verifying admin access&hellip;</div>
      </PageShell>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) {
    return (
      <PageShell>
        <div className="container py-14">
          <p className="eyebrow">Restricted</p>
          <h1 className="mt-3 font-display text-4xl font-medium text-foreground">Owner access only</h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-muted-foreground">
            You are signed in as {user.email}, but this area is limited to the Spiral
            Beyond Compliance owner account. Please sign out and try again with the
            authorised account.
          </p>
          <div className="mt-6 flex gap-2">
            <Button asChild variant="outline" className="rounded-sm"><Link to="/"><ArrowLeft className="h-4 w-4" /> Back to overview</Link></Button>
            <Button onClick={logout} className="rounded-sm" data-testid="admin-signout-btn"><LogOut className="h-4 w-4" /> Sign out</Button>
          </div>
        </div>
      </PageShell>
    );
  }

  const handleOpen = (q) => {
    setOpenedQuote(q);
    setTab("builder");
  };

  const handleNew = () => {
    setOpenedQuote(null);
    setTab("builder");
  };

  return (
    <PageShell hideFooter>
      <section className="container py-10">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="eyebrow">Admin · pricing & quotes</p>
            <h1 className="mt-2 font-display text-3xl font-medium text-foreground">Owner console</h1>
            <p className="mt-1 text-xs text-muted-foreground">
              Signed in as {user.email} · role {user.role}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleNew} className="rounded-sm" data-testid="new-quote-btn">
              <Plus className="h-4 w-4" />
              New quote
            </Button>
            <Button variant="ghost" onClick={logout} className="rounded-sm" data-testid="admin-signout-btn">
              <LogOut className="h-4 w-4" />
              Sign out
            </Button>
          </div>
        </div>

        <div className="mt-8 rounded-sm border border-amber-300/50 bg-amber-50/40 p-3 text-xs leading-relaxed text-amber-900">
          Internal only. All figures are professional-services guide prices and are
          not legal, accounting or taxation advice. Quotes require manual owner
          approval before they can be marked APPROVED or ISSUED. Nothing on this
          page is transmitted to clients automatically.
        </div>

        <Tabs value={tab} onValueChange={setTab} className="mt-6" data-testid="admin-tabs">
          <TabsList className="rounded-sm">
            <TabsTrigger value="builder" data-testid="tab-builder">Quote builder</TabsTrigger>
            <TabsTrigger value="drafts" data-testid="tab-drafts">Saved quotes</TabsTrigger>
            <TabsTrigger value="pricing" data-testid="tab-pricing">Pricing register</TabsTrigger>
            <TabsTrigger value="commercial" data-testid="tab-commercial">Commercial</TabsTrigger>
          </TabsList>

          <TabsContent value="builder" className="mt-6">
            <QuoteBuilder quote={openedQuote} onSaved={handleSaved} />
          </TabsContent>

          <TabsContent value="drafts" className="mt-6">
            <QuoteList onOpen={handleOpen} refreshKey={refreshKey} />
          </TabsContent>

          <TabsContent value="pricing" className="mt-6">
            <PricingRegister />
          </TabsContent>

          <TabsContent value="commercial" className="mt-6">
            <CommercialPanel />
          </TabsContent>
        </Tabs>
      </section>
    </PageShell>
  );
}
